import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/api/axiosInstance'

const LOCATION_KEY = 'user_location'

export interface UserLocation {
    id?: number
    districtId: number | null
    districtName: string
    address?: string
    latitude?: number | null
    longitude?: number | null
    city?: string
    label?: string
    is_default?: boolean
}

const adaptLegacyLocation = (raw: any): UserLocation => {
    if (!raw || typeof raw !== 'object') {
        return {
            districtId: null,
            districtName: '',
        }
    }

    return {
        id: raw.id ?? undefined,
        districtId: raw.districtId ?? null,
        districtName: raw.districtName ?? raw.district ?? '',
        address: raw.address ?? '',
        latitude: raw.latitude ?? null,
        longitude: raw.longitude ?? null,
        city: raw.city ?? 'Lima',
        label: raw.label ?? 'Mi ubicación',
        is_default: raw.is_default ?? true,
    }
}

export const useLocation = () => {
    const [location, setLocation] = useState<UserLocation | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const resolveDistrictIfNeeded = useCallback(async (loc: UserLocation): Promise<UserLocation> => {
        if (loc.districtId || !loc.districtName) {
            return loc
        }

        try {
            const response = await axiosInstance.get('/districts/resolve/', {
                params: { name: loc.districtName },
            })

            if (response.data?.id) {
                return {
                    ...loc,
                    districtId: response.data.id,
                    districtName: response.data.name,
                }
            }
        } catch (error) {
            console.warn('No se pudo resolver el distrito por nombre:', error)
        }

        return loc
    }, [])

    const loadStoredLocation = useCallback(async () => {
        try {
            const stored = localStorage.getItem(LOCATION_KEY)
            if (stored) {
                const locationData = adaptLegacyLocation(JSON.parse(stored))
                const resolved = await resolveDistrictIfNeeded(locationData)
                console.log('[useLocation] loaded from storage', resolved)
                setLocation(resolved)
                if (resolved !== locationData) {
                    localStorage.setItem(LOCATION_KEY, JSON.stringify(resolved))
                }
            }
        } catch (err) {
            console.error('Error loading location:', err)
        } finally {
            setIsLoading(false)
        }
    }, [resolveDistrictIfNeeded])

    useEffect(() => {
        loadStoredLocation()

        const handleCustomUpdate = (e: Event) => {
            const custom = e as CustomEvent<UserLocation>
            console.log('[useLocation] custom event received', custom.detail)
            if (custom.detail) {
                const normalized = adaptLegacyLocation(custom.detail)
                resolveDistrictIfNeeded(normalized).then((resolved) => {
                    setLocation(resolved)
                    try {
                        localStorage.setItem(LOCATION_KEY, JSON.stringify(resolved))
                    } catch (storageError) {
                        console.error('Error guardando ubicación normalizada en storage:', storageError)
                    }
                })
            } else {
                loadStoredLocation()
            }
        }

        const handleStorage = (e: StorageEvent) => {
            console.log('[useLocation] storage event', e.key)
            if (e.key === LOCATION_KEY) {
                loadStoredLocation()
            }
        }

        window.addEventListener('domber:location-updated', handleCustomUpdate as EventListener)
        window.addEventListener('storage', handleStorage)

        return () => {
            window.removeEventListener('domber:location-updated', handleCustomUpdate as EventListener)
            window.removeEventListener('storage', handleStorage)
        }
    }, [loadStoredLocation, resolveDistrictIfNeeded])

    const getCurrentLocation = async (): Promise<UserLocation | null> => {
        try {
            setIsLoading(true)
            setError(null)

            if (!navigator.geolocation) {
                setError('Tu navegador no soporta geolocalización')
                return null
            }

            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 300000
                })
            })

            const userLoc: UserLocation = {
                districtId: null,
                districtName: '',
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                address: '',
                city: 'Lima',
                label: 'Mi ubicación',
                is_default: true
            }

            console.log('[useLocation] geolocation success', userLoc)
            setLocation(userLoc)
            localStorage.setItem(LOCATION_KEY, JSON.stringify(userLoc))
            setIsLoading(false)
            return userLoc
        } catch (err: any) {
            console.error('Error getting location:', err)
            setError(err.message || 'Error obteniendo la ubicación')
            setIsLoading(false)
            return null
        }
    }

    const setManualLocation = useCallback(async (loc: UserLocation) => {
        const normalized = adaptLegacyLocation(loc)
        const resolved = await resolveDistrictIfNeeded(normalized)
        console.log('[useLocation] setManualLocation', resolved)
        setLocation(resolved)
        localStorage.setItem(LOCATION_KEY, JSON.stringify(resolved))
        window.dispatchEvent(new CustomEvent('domber:location-updated', { detail: resolved }))
    }, [resolveDistrictIfNeeded])

    const clearLocation = async () => {
        console.log('[useLocation] clearLocation')
        setLocation(null)
        localStorage.removeItem(LOCATION_KEY)
        window.dispatchEvent(new CustomEvent('domber:location-updated'))
    }

    return {
        location,
        isLoading,
        error,
        getCurrentLocation,
        setManualLocation,
        clearLocation,
    }
}

export const useCreateUserLocation = () => {
    const queryClient = useQueryClient()
    
    return useMutation({
        mutationFn: async (locationData: UserLocation) => {
            const response = await axiosInstance.post('/locations/', {
                label: locationData.label || 'Mi ubicación',
                address: locationData.address || '',
                latitude: locationData.latitude != null ? Number(Number(locationData.latitude).toFixed(6)) : null,
                longitude: locationData.longitude != null ? Number(Number(locationData.longitude).toFixed(6)) : null,
                city: locationData.city || 'Lima',
                district: locationData.districtName || '',
                district_id: locationData.districtId ?? null,
                reference: '',
                is_default: true
            })
            return response.data
        },
        onSuccess: (data, variables) => {
            const locationData: UserLocation = {
                id: data.id,
                districtId: variables.districtId ?? null,
                districtName: data.district || '',
                latitude: data.latitude,
                longitude: data.longitude,
                address: data.address,
                city: data.city,
                label: data.label,
                is_default: data.is_default
            }
            localStorage.setItem(LOCATION_KEY, JSON.stringify(locationData))
            window.dispatchEvent(new CustomEvent('domber:location-updated', { detail: locationData }))
            
            queryClient.invalidateQueries({ queryKey: ['user-locations'] })
        }
    })
}

export const useUserLocations = () => {
    return useQuery({
        queryKey: ['user-locations'],
        queryFn: async () => {
            const response = await axiosInstance.get('/locations/')
            return response.data
        }
    })
}
